import { AlertColor, Box, Grid, Typography } from '@mui/material';
import Notification from 'components/Notification';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Config from 'utils/Config';
import Navigation from 'utils/hooks/Navigation';
import { pageTitleSx } from 'utils/styles/pageHeader';
import strings from '../../../../../common/Translation/Translate';
import PageLoader from '../../../../../components/PageLoader';
import axiosInstance from '../../../../../utils/axios';
import { routes } from '../../../../../utils/constants/routes';
import { URLS } from '../../../../../utils/constants/urls';
import { createDropdownData } from '../../../../../utils/validators/HelperFunctions';
import AddEdit from '../../../CommonPage/AddEdit';
import './AddEditEmailEventConfiguration.css';
import { AddEditEmailEventConfigForm } from './AddEditEmailEventConfigurationForm';

const AddEditEmailEventConfig: FC = () => {
  const { id } = useParams() as any;
  const [pageResponse, setPageResponse] = useState<any>([]);
  const [eventTypes, setEventTypes] = useState<any>([]);
  const [mailTypes, setMailTypes] = useState<any>([]);
  const [roles, setRoles] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [value, setValue] = useState(0);
  const navigate = useNavigate();
  const [customId, setCustomId] = useState<number>();
  const [attachment, setAttachment] = useState<any>([]);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const getInitialResponse = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.emailEventConfigById(id));
      const toRoleIds = roles?.filter((item: any) => data.toRoleIds?.split(',').includes(item.id.toString()));
      const ccRoleIds = roles?.filter((item: any) => data.ccRoleIds?.split(',').includes(item.id.toString()));
      const setNewData = {
        ...data,
        eventType: data?.eventType?.value,
        mailType: data?.mailType?.value,
        toRoleIds: createDropdownData(toRoleIds, ['id', 'name']),
        ccRoleIds: createDropdownData(ccRoleIds, ['id', 'name']),
      };
      setPageResponse(setNewData);
      setAttachment(data.attachments);
      const fileData = data.attachments;
      const newFiles = attachment.length > 0 ? attachment : [];
      if (fileData.length > 0) {
        fileData.forEach((item: any) => {
          const objFile = {} as any;
          objFile.preview = fileData ? Config.BaseURL + item?.url : null;
          objFile.name = item?.fileName || '';
          objFile.id = item.id;
          newFiles.push(objFile);
        });
      }
      const unique = newFiles?.filter((obj: any, index: number) => {
        return index === newFiles.findIndex((o: any) => obj.name === o.name);
      });

      setAttachment(unique);
    } catch (error: any) { }
    finally {
      setLoading(false);
    }
  }, [id, roles, value]);



  const getDropdowns = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.emailEventConfigDropdowns);
      const eventTypesFilter = data.eventTypes?.map((item: any) => {
        return { label: item.name, value: item.id };
      });
      const mailTypesFilter = data.mailTemplates?.map((item: any) => {
        return { label: item.name, value: item.name };
      });
      setEventTypes(eventTypesFilter);
      setMailTypes(mailTypesFilter);
      setRoles(data.roles);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await getDropdowns();
    })();
  }, [getDropdowns]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getInitialResponse();
        setCustomId(id);
      }
    })();
  }, [id, getInitialResponse]);

  const pageConfig = {
    title: id || customId ? strings.email_event_config_title_edit : strings.email_event_config_title_add,
    successMessage: id || customId ? strings.common_update_message : strings.common_save_message,
    addEditApiUrl: id || customId ? URLS.emailEventConfigById(id || customId) : URLS.emailEventConfigAdd,
    initialApi: URLS.emailEventConfigById(id || customId),
    formModel: AddEditEmailEventConfigForm(strings, eventTypes, mailTypes, id || customId),
    isCustomResponse: true,
    navigateToPage: ``,
    hideClose: true,
  };

  const a = (value: string) => {
    if (Array.isArray(value))
      return value
    else if (value === '')
      return []
    else return value.split(',').map((item: any) => item.trim())
  }

  const fetchValue = (arr: any) => arr.map((item: any) => parseInt(item.value));
  const handleCustomResponse = (custom: any) => {
    const data = {
      ...custom,
      toEmailAddresses: a(custom.toEmailAddresses),
      ccEmailAddresses: a(custom.ccEmailAddresses),
      toRoleIds: custom.toRoleIds === '' ? [] : fetchValue(custom.toRoleIds),
      ccRoleIds: custom.ccRoleIds === '' ? [] : fetchValue(custom.ccRoleIds),
    };
    return data;
  };

  const customCallbackResponse = (callbackValue: any) => {
    setCustomId(callbackValue.id);
    setValue(1);
    navigate(`${routes.editEmailEventConfig}/${callbackValue.id}`, { replace: true });
  };


  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };


  return (
    <>
      {loading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <Grid container>
        <Grid item xs={10}>
          <Typography sx={pageTitleSx}> {pageConfig.title} </Typography>
        </Grid>
        <Grid item textAlign="right" xs={2}>
          <Navigation url={routes.emailEventConfig} />
        </Grid>
      </Grid>
      <Box mt={4}>
        <AddEdit
          dropdownValue={{
            toRoleIds: createDropdownData(roles, ['id', 'name']),
            ccRoleIds: createDropdownData(roles, ['id', 'name']),
          }}
          pageConfig={pageConfig}
          pageResponse={pageResponse}
          handleCustomResponse={handleCustomResponse as any}
          customCallbackResponse={customCallbackResponse}
        />
      </Box>

    </>
  );
};
export default AddEditEmailEventConfig;
